import type {Component, ComponentModelDefinition, ComponentView, ComponentViewDefinition} from 'grapesjs';
import {EMAIL_MAKER_COMMANDS, EMAIL_MAKER_COMPONENT_TYPES, EMAIL_MAKER_PROPS, MAX_CONTENT_WIDTH, NEW_TEMPLATE_NAME} from '@EmailMakerReact/email-maker-react.constants';
import type {ComponentRegistrationContext} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/helpers/component-helpers';
import {matchByTagAndAttr} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/helpers/component-helpers';

export function registerContainerTypes({editor, unstylableEmailStyles, initRemovalOfUnsupportedCssProperties}: ComponentRegistrationContext): void {
  const cmpt = editor.Components;
  cmpt.addType(EMAIL_MAKER_COMPONENT_TYPES.container.PMW_WRAPPER, {
    extend: EMAIL_MAKER_COMPONENT_TYPES.default.TABLE,
    model: {
      defaults: {
        trackingName: 'Email',
        toolbar: [],
        removable: false,
        highlightable: false,
        hoverable: false,
        badgable: false,
        copyable: false,
        draggable: [EMAIL_MAKER_COMPONENT_TYPES.default.WRAPPER],
        droppable: false,
        unstylable: unstylableEmailStyles,
        [EMAIL_MAKER_PROPS.HAS_BORDER_CONTROLS]: false,
        attributes: {
          'data-is-pmw-wrapper': '1',
          'data-pmw-type': EMAIL_MAKER_COMPONENT_TYPES.container.PMW_WRAPPER,
          cellspacing: 0,
          cellpadding: 0,
        },
        style: {
          'background-color': '#FAFAFA',
          height: '100% !important',
          width: '100% !important',
        },
        [EMAIL_MAKER_PROPS.TEMPLATE_NAME]: NEW_TEMPLATE_NAME,
        components: [
          {
            type: EMAIL_MAKER_COMPONENT_TYPES.table.PMW_TBODY,
            components: [
              {
                type: EMAIL_MAKER_COMPONENT_TYPES.table.PMW_ROW,
                style: {
                  height: '100%',
                },
                components: [
                  {
                    type: EMAIL_MAKER_COMPONENT_TYPES.container.PMW_WRAPPER_CELL,
                    style: {
                      height: '100%',
                    },
                    stylable: false,
                    removable: false,
                    selectable: false,
                    hoverable: false,
                    badgable: false,
                    components: [
                      {
                        type: EMAIL_MAKER_COMPONENT_TYPES.container.PMW_CONTENT,
                        components: [
                          {
                            type: EMAIL_MAKER_COMPONENT_TYPES.table.PMW_TBODY,
                            components: [
                              {
                                type: EMAIL_MAKER_COMPONENT_TYPES.table.PMW_ROW,
                                components: [
                                  {
                                    type: EMAIL_MAKER_COMPONENT_TYPES.container.PMW_CONTENT_CONTAINER,
                                    stylable: false,
                                    removable: false,
                                    selectable: false,
                                    hoverable: false,
                                    badgable: false,
                                    droppable: true,
                                  },
                                ],
                              },
                            ],
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      init(this: ComponentModelDefinition & Component) {
        initRemovalOfUnsupportedCssProperties(this);
      },
    },
    isComponent: matchByTagAndAttr('TABLE', 'data-is-pmw-wrapper', EMAIL_MAKER_COMPONENT_TYPES.container.PMW_WRAPPER),
  });

  cmpt.addType(EMAIL_MAKER_COMPONENT_TYPES.container.PMW_CONTENT_CONTAINER, {
    extend: EMAIL_MAKER_COMPONENT_TYPES.default.CELL,
    model: {
      defaults: {
        tagName: 'td',
        trackingName: null,
        toolbar: [],
        removable: false,
        highlightable: false,
        hoverable: false,
        badgable: false,
        copyable: false,
        draggable: false,
        droppable: true,
        stylable: false,
        unstylable: unstylableEmailStyles,
        [EMAIL_MAKER_PROPS.HAS_BORDER_CONTROLS]: false,
        attributes: {
          'data-pmw-type': EMAIL_MAKER_COMPONENT_TYPES.container.PMW_CONTENT_CONTAINER,
          'data-is-pmw-content-container': 1,
          align: 'middle',
        },
        style: {
          background: 'none',
          width: '100%',
          margin: '0',
          padding: '0',
          height: '100%',
          'min-height': '100%',
          'max-height': 'none',
          'vertical-align': 'top',
        },
      },
      init(this: ComponentModelDefinition & Component) {
        initRemovalOfUnsupportedCssProperties(this);
      },
    },
    isComponent: matchByTagAndAttr('TABLE', 'data-is-pmw-content-container', EMAIL_MAKER_COMPONENT_TYPES.container.PMW_CONTENT_CONTAINER),
  });

  cmpt.addType(EMAIL_MAKER_COMPONENT_TYPES.container.PMW_CONTENT, {
    extend: EMAIL_MAKER_COMPONENT_TYPES.default.TABLE,
    model: {
      defaults: {
        tagName: 'table',
        trackingName: 'Content',
        toolbar: [],
        removable: false,
        highlightable: false,
        selectable: false,
        hoverable: false,
        badgable: false,
        copyable: false,
        draggable: [EMAIL_MAKER_COMPONENT_TYPES.container.PMW_CONTENT_CONTAINER],
        droppable: false,
        stylable: true,
        [EMAIL_MAKER_PROPS.HAS_BORDER_CONTROLS]: false,
        attributes: {
          'data-is-pmw-content': '1',
          'data-pmw-type': EMAIL_MAKER_COMPONENT_TYPES.container.PMW_CONTENT,
          class: 'pmw-content-container',
          cellspacing: 0,
        },
        gettyImageCount: 0,
        gettyVideoCount: 0,
        style: {
          'background-color': '#FFFFFF',
          'border-color': '#FFFFFF',
          'border-width': '0px',
          'font-family': 'Helvetica, sans-serif',
          'margin-left': 'auto',
          'margin-right': 'auto',
          height: '100%',
          'min-width': 'min(600px, 100%)',
          'min-height': '100%',
          'max-width': MAX_CONTENT_WIDTH,
          'max-height': 'none',
          'vertical-align': 'top',
          width: '80%',
        },
        unstylable: unstylableEmailStyles,
      },
      init(this: ComponentModelDefinition & Component) {
        initRemovalOfUnsupportedCssProperties(this);
      },
    },
    view: {
      onRender(this: ComponentViewDefinition & ComponentView, {model}: {model: Component}) {
        editor.runCommand(EMAIL_MAKER_COMMANDS.HANDLE_TABLE_PADDING, {
          component: model,
        });

        editor.runCommand(EMAIL_MAKER_COMMANDS.UPDATE_CONTENT_MAX_WIDTH, {
          component: model,
        });
      },
      init(this: ComponentViewDefinition & ComponentView) {
        this.listenTo(this, 'change:style', () => {
          editor.runCommand(EMAIL_MAKER_COMMANDS.SYNC_TABLE_TBODY_COLORS, {
            component: this,
          });
        });
      },
    },
    isComponent: matchByTagAndAttr('TABLE', 'data-is-pmw-content', EMAIL_MAKER_COMPONENT_TYPES.container.PMW_CONTENT),
  });

  cmpt.addType(EMAIL_MAKER_COMPONENT_TYPES.container.PMW_WRAPPER_CELL, {
    extend: EMAIL_MAKER_COMPONENT_TYPES.default.CELL,
    model: {
      defaults: {
        tagName: 'td',
        trackingName: null,
        toolbar: [],
        removable: false,
        selectable: false,
        highlightable: false,
        hoverable: false,
        badgable: false,
        draggable: [EMAIL_MAKER_COMPONENT_TYPES.default.WRAPPER],
        droppable: false,
        stylable: false,
        unstylable: unstylableEmailStyles,
        [EMAIL_MAKER_PROPS.HAS_BORDER_CONTROLS]: false,
        attributes: {
          'data-is-pmw-wrapper-cell': '1',
          'data-pmw-type': EMAIL_MAKER_COMPONENT_TYPES.container.PMW_WRAPPER_CELL,
          cellspacing: 0,
          align: 'middle',
        },
        style: {
          background: 'none',
          width: '100%',
          margin: 'auto',
          height: 'auto',
          'min-height': '100%',
          'max-height': 'none',
          'font-family': 'Helvetica, sans-serif',
          border: 'none',
          'vertical-align': 'top',
        },
      },
    },
    isComponent: matchByTagAndAttr('TD', 'data-is-pmw-wrapper-cell', EMAIL_MAKER_COMPONENT_TYPES.container.PMW_WRAPPER_CELL),
  });

  // Must register last: isComponent matcher overlaps default.TEXT / PMW_DIV; GrapesJS resolves last-wins.
  cmpt.addType(EMAIL_MAKER_COMPONENT_TYPES.misc.PMW_READ_ONLY_FOOTER, {
    model: {
      defaults: {
        name: window.i18next.t('pmwjs_footer'),
        trackingName: null,
        deletable: false,
        highlightable: true,
        hoverable: true,
        selectable: true,
        badgable: true,
        droppable: false,
        draggable: false,
        copyable: false,
        editable: false,
        toolbar: [],
        [EMAIL_MAKER_PROPS.HAS_BORDER_CONTROLS]: false,
        attributes: {
          'data-is-readonly-text': true,
          'data-pmw-type': EMAIL_MAKER_COMPONENT_TYPES.misc.PMW_READ_ONLY_FOOTER,
        },
      },
    },
    isComponent: (el: HTMLElement) => {
      if (el instanceof Element && el.getAttribute('data-is-readonly-text')) {
        return {
          type: EMAIL_MAKER_COMPONENT_TYPES.misc.PMW_READ_ONLY_FOOTER,
        };
      }

      return false;
    },
  });
}
